/**
 * Authentication Context
 * 
 * Provides authentication state and methods throughout the application.
 * Handles session persistence and automatic token refresh.
 */

import {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import {
  signUp as cognitoSignUp,
  confirmSignUp as cognitoConfirmSignUp,
  resendConfirmationCode as cognitoResendCode,
  signIn as cognitoSignIn,
  signOut as cognitoSignOut,
  forgotPassword as cognitoForgotPassword,
  confirmForgotPassword as cognitoConfirmForgotPassword,
  getSession,
  getUserInfo,
  getAuthTokens,
  getErrorMessage,
  type SignUpParams,
  type SignInParams,
  type CognitoUserInfo,
  type AuthTokens,
} from '../lib/cognito';

// API URL from environment
const API_URL = import.meta.env.VITE_API_URL || '';

// User profile from our database
export interface UserProfile {
  id: string;
  cognitoSub: string;
  email: string;
  displayName: string | null;
  fullName: string | null;
  avatarUrl: string | null;
  accountType: 'explorer' | 'owner' | 'admin';
  winePreferences: {
    types?: string[];
    grapes?: string[];
  };
  privacySettings: {
    showVisited?: 'public' | 'friends' | 'private';
    showWishlist?: 'public' | 'friends' | 'private';
  };
  profileCompleted: boolean;
  createdAt: string;
}

// Auth context state
export interface AuthState {
  // State
  isAuthenticated: boolean;
  isLoading: boolean;
  user: CognitoUserInfo | null;
  profile: UserProfile | null;
  tokens: AuthTokens | null;
  error: string | null;
  
  // Auth methods
  signUp: (params: SignUpParams) => Promise<void>;
  confirmSignUp: (email: string, code: string) => Promise<void>;
  resendConfirmationCode: (email: string) => Promise<void>;
  signIn: (params: SignInParams) => Promise<void>;
  signOut: () => void;
  forgotPassword: (email: string) => Promise<void>;
  confirmForgotPassword: (email: string, code: string, newPassword: string) => Promise<void>;
  
  // Profile methods
  fetchProfile: () => Promise<void>;
  updateProfile: (data: Partial<UserProfile>) => Promise<void>;
  checkDisplayNameAvailable: (displayName: string) => Promise<boolean>;
  
  // Utilities
  clearError: () => void;
  getAuthHeader: () => Promise<Record<string, string>>;
}

// Create context with undefined default
export const AuthContext = createContext<AuthState | undefined>(undefined);

// Provider props
interface AuthProviderProps {
  children: ReactNode;
}

/**
 * Auth Provider Component
 */
export function AuthProvider({ children }: AuthProviderProps) {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [user, setUser] = useState<CognitoUserInfo | null>(null);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [tokens, setTokens] = useState<AuthTokens | null>(null);
  const [error, setError] = useState<string | null>(null);

  /**
   * Clear error state
   */
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  /**
   * Get authorization header for API calls
   */
  const getAuthHeader = useCallback(async (): Promise<Record<string, string>> => {
    const currentTokens = await getAuthTokens();
    if (!currentTokens) {
      return {};
    }
    return {
      Authorization: `Bearer ${currentTokens.idToken}`,
    };
  }, []);

  /**
   * Fetch user profile from our API
   */
  const fetchProfile = useCallback(async () => {
    try {
      const headers = await getAuthHeader();
      if (!headers.Authorization) {
        return;
      }

      const response = await fetch(`${API_URL}/users/me`, {
        headers: {
          ...headers,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        setProfile(data.data);
      } else if (response.status === 404) {
        // Profile not found - user needs to complete profile
        setProfile(null);
      }
    } catch (err) {
      console.error('Failed to fetch profile:', err);
    }
  }, [getAuthHeader]);

  /**
   * Check current session on mount
   */
  useEffect(() => {
    async function checkSession() {
      try {
        const session = await getSession();
        
        if (session && session.isValid()) {
          const userInfo = await getUserInfo();
          const authTokens = await getAuthTokens();
          
          setUser(userInfo);
          setTokens(authTokens);
          setIsAuthenticated(true);
          
          // Fetch profile from our API
          await fetchProfile();
        }
      } catch (err) {
        console.error('Session check failed:', err);
      } finally {
        setIsLoading(false);
      }
    }

    checkSession();
  }, [fetchProfile]);

  /**
   * Sign up a new user
   */
  const signUp = useCallback(async (params: SignUpParams) => {
    setError(null);
    setIsLoading(true);
    
    try {
      await cognitoSignUp(params);
      // Don't set authenticated - user needs to verify email first
    } catch (err) {
      const message = getErrorMessage(err);
      setError(message);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, []);

  /**
   * Confirm sign up with verification code
   */
  const confirmSignUp = useCallback(async (email: string, code: string) => {
    setError(null);
    setIsLoading(true);
    
    try {
      await cognitoConfirmSignUp(email, code);
      // User is now confirmed, but not signed in yet
    } catch (err) {
      const message = getErrorMessage(err);
      setError(message);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, []);

  /**
   * Resend confirmation code
   */
  const resendConfirmationCode = useCallback(async (email: string) => {
    setError(null);
    
    try {
      await cognitoResendCode(email);
    } catch (err) {
      const message = getErrorMessage(err);
      setError(message);
      throw err;
    }
  }, []);

  /**
   * Sign in a user
   */
  const signIn = useCallback(async (params: SignInParams) => {
    setError(null);
    setIsLoading(true);
    
    try {
      await cognitoSignIn(params);
      
      // Get user info and tokens
      const userInfo = await getUserInfo();
      const authTokens = await getAuthTokens();
      
      setUser(userInfo);
      setTokens(authTokens);
      setIsAuthenticated(true);
      
      // Fetch profile from our API
      await fetchProfile();
    } catch (err) {
      const message = getErrorMessage(err);
      setError(message);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, [fetchProfile]);

  /**
   * Sign out the current user
   */
  const signOut = useCallback(() => {
    cognitoSignOut();
    setUser(null);
    setProfile(null);
    setTokens(null);
    setIsAuthenticated(false);
    setError(null);
  }, []);

  /**
   * Initiate forgot password flow
   */
  const forgotPassword = useCallback(async (email: string) => {
    setError(null);
    
    try {
      await cognitoForgotPassword(email);
    } catch (err) {
      const message = getErrorMessage(err);
      setError(message);
      throw err;
    }
  }, []);

  /**
   * Confirm forgot password with new password
   */
  const confirmForgotPassword = useCallback(async (
    email: string,
    code: string,
    newPassword: string
  ) => {
    setError(null);
    
    try {
      await cognitoConfirmForgotPassword(email, code, newPassword);
    } catch (err) {
      const message = getErrorMessage(err);
      setError(message);
      throw err;
    }
  }, []);

  /**
   * Update user profile
   */
  const updateProfile = useCallback(async (data: Partial<UserProfile>) => {
    setError(null);
    
    try {
      const headers = await getAuthHeader();
      
      const response = await fetch(`${API_URL}/users/me`, {
        method: 'PUT',
        headers: {
          ...headers,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to update profile');
      }

      const result = await response.json();
      setProfile(result.data);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to update profile';
      setError(message);
      throw err;
    }
  }, [getAuthHeader]);

  /**
   * Check if display name is available
   */
  const checkDisplayNameAvailable = useCallback(async (displayName: string): Promise<boolean> => {
    try {
      const headers = await getAuthHeader();
      
      const response = await fetch(
        `${API_URL}/users/check-display-name/${encodeURIComponent(displayName)}`,
        { headers }
      );

      if (!response.ok) {
        return false;
      }

      const result = await response.json();
      // API returns { data: { available: boolean } }
      return result.data?.available ?? false;
    } catch {
      return false;
    }
  }, [getAuthHeader]);

  // Memoize context value to prevent unnecessary re-renders
  const contextValue = useMemo<AuthState>(() => ({
    isAuthenticated,
    isLoading,
    user,
    profile,
    tokens,
    error,
    signUp,
    confirmSignUp,
    resendConfirmationCode,
    signIn,
    signOut,
    forgotPassword,
    confirmForgotPassword,
    fetchProfile,
    updateProfile,
    checkDisplayNameAvailable,
    clearError,
    getAuthHeader,
  }), [
    isAuthenticated,
    isLoading,
    user,
    profile,
    tokens,
    error,
    signUp,
    confirmSignUp,
    resendConfirmationCode,
    signIn,
    signOut,
    forgotPassword,
    confirmForgotPassword,
    fetchProfile,
    updateProfile,
    checkDisplayNameAvailable,
    clearError,
    getAuthHeader,
  ]);

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
}
